
import { useState, useEffect, useRef } from 'react';

export default function AtelierFeatures() {
  const features = [
    {
      icon: 'ri-compass-3-line',
      title: '無段階角度調整機構',
      desc: 'イラスト制作からノートテイキングまで、あらゆるクリエイティブワークに最適な角度を実現。マグネット式ヒンジで自在にポジショニング。',
    },
    {
      icon: 'ri-pencil-line',
      title: 'Apple Pencil専用ホルダー',
      desc: 'Apple Pencilを常にベストポジションに。充電しながら収納でき、使いたい時にすぐ手に取れる設計。',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'プレミアムレザー仕上げ',
      desc: '厳選されたヴィーガンレザーを採用。使い込むほどに手に馴染み、経年変化を楽しめる上質な素材感。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '360°プロテクション',
      desc: 'iPadを全方位からしっかり保護。衝撃吸収構造と精密なフィッティングで、持ち運びも安心。',
    },
    {
      icon: 'ri-weight-line',
      title: '超軽量設計',
      desc: 'スタンド機能を備えながらも驚きの軽さ。カフェでも、移動中でも、どこでもアトリエに。',
    },
    {
      icon: 'ri-magic-line',
      title: 'ワンタッチ着脱',
      desc: 'マグネット式の着脱機構で、iPadの取り付け・取り外しがワンタッチ。スムーズなワークフローを実現。',
    },
  ];

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    // Guard against browsers that don't support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-20 sm:py-28 bg-[#0A0A0A] relative"
      aria-labelledby="af-features-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div
          className={`text-center mb-14 sm:mb-18 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="af-features-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight"
          >
            すべてが、クリエイターのために
          </h2>
          <p className="text-sm sm:text-base text-white/40 font-light max-w-2xl mx-auto">
            Atelier Folioは、iPadでのクリエイティブワークを次のレベルへ引き上げるために設計されました。
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`group p-6 sm:p-8 bg-white/5 border border-white/5 rounded-xl hover:bg-white/6 hover:border-white/10 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100 + 200}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#8E7AB5]/10 rounded-xl mb-4 group-hover:bg-[#8E7AB5]/20 transition-colors">
                <i className={`${f.icon} text-[#8E7AB5] text-xl`} aria-hidden="true"></i>
              </div>
              <h3 className="text-white font-medium text-sm sm:text-base mb-2">{f.title}</h3>
              <p className="text-white/35 text-xs sm:text-sm font-light leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
